// Stadium Trial — tier / encounter configuration (content only, easy to tune).
//
// A tier is one full run of 10 encounters:
//   stages 1-4  = mobs (fixed budget, ramped by ROOM_MULTIPLIER)
//   stage  5    = boss  (MANUAL stat block — first level of the tier's range)
//   stages 6-9  = mobs (fixed budget, ramped by ROOM_MULTIPLIER)
//   stage  10   = boss  (MANUAL stat block — last level of the tier's range)
//
// Mobs = a full-RARE player at the tier's MIN level, the same base for the whole
// run, only scaled per-room by ROOM_MULTIPLIER (progression WITHIN the run). The
// level does NOT climb between rooms — the room multiplier is the only ramp, so
// difficulty doesn't stack twice.
//
// Bosses = defined BY HAND (`statBlock`), no auto-generation. Each carries a
// full-EPIC reference total (for its level) in a comment so you know the ballpark
// budget to aim for when editing the 4 stats.
//
// The combat engine (simulateCombat / enemyStatsFor) consumes this: mobs spread
// `mobBudget` over the enemy's statWeights (shape: striker/tank/…), bosses use
// `statBlock` verbatim. `damageMultiplier` scales the enemy's hits.

import { RARITY_BASE } from "../../../constants/items";
import { levelScaling } from "../../../engine/itemEngine";
import type { PveBossEffectId } from "./pveBossEffects";

export type EncounterKind = "mob" | "boss";

// ── Balance patch version ─────────────────────────────────────────────────────
// Bump by hand on EVERY balance change (boss stats, mob scale, boost effects,
// combat formulas…). Stamped on every pve_fights telemetry row so the dashboard
// can segment before/after a patch instead of averaging across versions.
export const PVE_CONFIG_VERSION = 1;

// ── Full-equipped power reference ─────────────────────────────────────────────
// A full-equipped player carries 6 stat-bearing slots (head, top, shoes, ball,
// background, familiar) over the 4 base stats (10 each = 40):
//   power ≈ 40 + 6 × RARITY_BASE[rarity] × levelScaling(level)
const BASE_STATS = 4 * 10;   // EMPTY_STATS floor (see combat.ts)
const STAT_SLOTS = 6;

export function estimateFullEquippedPower(level: number, rarity: keyof typeof RARITY_BASE): number {
  return Math.round(BASE_STATS + STAT_SLOTS * RARITY_BASE[rarity] * levelScaling(level));
}

// Full-EPIC budget per level (208 + 8.4·L), for boss-tuning reference:
//   L1=216  L2=225  L3=233  L4=242  L5=250  L6=258
//   L7=267  L8=275  L9=284  L10=292 L11=300 L12=309

// ── Room ramp (mobs only) — progression WITHIN a run ──────────────────────────
// Stage 5 & 10 are bosses (not listed). Tune the run's internal difficulty curve
// here in ONE place.
export const ROOM_MULTIPLIER: Record<number, number> = {
  1: 1.00, 2: 1.02, 3: 1.04, 4: 1.06,
  6: 1.08, 7: 1.10, 8: 1.12, 9: 1.14,
};

// ── Global mob power dial ─────────────────────────────────────────────────────
// Mobs are built as a fraction of a full-`mobRarity` player at the tier level.
// A raw full-gear mob (scale 1.0) has ~the same budget as an at-level player, and
// the per-stat combat needs a ~1.2× edge to win reliably — so mobs would behave
// like mini-bosses (and post-boss5 mobs become a second wall). Scaling them down
// turns mobs into attrition speed-bumps and keeps stages 5 & 10 as the real gear
// checks. Lower = easier mobs (0.70 ≈ easy, 1.00 ≈ mini-bosses). Single knob.
export const MOB_POWER_SCALE = 0.80;

/** Manually-authored enemy stats (bosses). */
export interface PveStatBlock {
  physique: number;   // → damage per hit
  technique: number;  // → crit chance
  vitesse: number;    // → HP (vitesse × 10)
  defense: number;    // → incoming damage reduction
}

export interface PveEncounter {
  stage: number;                 // 1..10
  kind: EncounterKind;
  enemyId: string;               // → PVE_ENEMIES (shape via statWeights, art, tags)
  name?: string;                 // display override (bosses)
  mobBudget?: number;            // mob: fixed stat budget (room multiplier already applied)
  statBlock?: PveStatBlock;      // boss: explicit 4 stats (bypasses statWeights)
  damageMultiplier?: number;     // scales THIS enemy's outgoing damage
  effects?: PveBossEffectId[];   // boss: dynamic combat effects (→ pveBossEffects.ts)
  tags?: string[];
  weaknesses?: string[];
  skills?: string[];             // declared only — not implemented yet
}

export interface PveTier {
  tier: number;
  tierName: string;
  recommendedLevel?: string;     // display only (e.g. "Lv 1-3")
  levelRange: [number, number];  // [min, max] tuning target (telemetry: level-gap)
  encounters: PveEncounter[];    // length 10, stage 1..10
}

// ── Tier factory ──────────────────────────────────────────────────────────────
// Generates the 8 mobs automatically (full-rare at the tier's MIN level, ramped
// per room) and slots in the two MANUAL boss stat blocks. Edit a tier by tweaking
// its config below — never hand-write the mob encounters.
const MOB_IDS = ["t1_mob_striker", "t1_mob_runner", "t1_mob_wall", "t1_mob_playmaker"];

interface BossConfig extends PveStatBlock {
  name: string;
  damageMultiplier?: number;
  effects?: PveBossEffectId[];
}

interface TierConfig {
  tier: number;
  tierName: string;
  levelRange: [number, number];              // [min, max] — boss5 = min, boss10 = max
  mobRarity: keyof typeof RARITY_BASE;       // mobs' full-gear rarity (base budget)
  boss5: BossConfig;                          // MANUAL — stage 5
  boss10: BossConfig;                         // MANUAL — stage 10
}

function bossEncounter(stage: number, enemyId: string, cfg: BossConfig, tags: string[]): PveEncounter {
  return {
    stage, kind: "boss", enemyId, name: cfg.name,
    statBlock: { physique: cfg.physique, technique: cfg.technique, vitesse: cfg.vitesse, defense: cfg.defense },
    damageMultiplier: cfg.damageMultiplier ?? 1,
    effects: cfg.effects,
    tags,
  };
}

function makeTier(cfg: TierConfig): PveTier {
  const [lo, hi] = cfg.levelRange;
  // Mob base = a full-`mobRarity` player at the tier's MIN level (same for the run),
  // toned down by MOB_POWER_SCALE, then ramped per room by ROOM_MULTIPLIER.
  const mobBase = estimateFullEquippedPower(lo, cfg.mobRarity) * MOB_POWER_SCALE;
  const mob = (stage: number, idx: number): PveEncounter => ({
    stage, kind: "mob",
    enemyId: MOB_IDS[idx % MOB_IDS.length],
    mobBudget: Math.round(mobBase * (ROOM_MULTIPLIER[stage] ?? 1)),
  });
  return {
    tier: cfg.tier,
    tierName: cfg.tierName,
    recommendedLevel: `Lv ${lo}-${hi}`,
    levelRange: [lo, hi],
    encounters: [
      mob(1, 0), mob(2, 1), mob(3, 2), mob(4, 3),
      bossEncounter(5, "t1_boss_captain", cfg.boss5, ["boss"]),
      mob(6, 0), mob(7, 1), mob(8, 2), mob(9, 3),
      bossEncounter(10, "t1_boss_ace", cfg.boss10, ["boss", "final"]),
    ],
  };
}

// ── Auto-scaled tier factory (tiers 11+) ──────────────────────────────────────
// The two boss shapes below match the boss enemies' statWeights (pveEnemies.ts).
// Distributing the full-EPIC budget at the boss's level over these weights REPRO-
// DUCES the hand-authored tiers 1-10 (to ±1), so the extended tiers keep the exact
// same difficulty curve — the level range climbing is the only ramp, as before.
const BOSS5_SHAPE:  PveStatBlock = { physique: 0.30, technique: 0.25, vitesse: 0.25, defense: 0.20 }; // Rival Captain
const BOSS10_SHAPE: PveStatBlock = { physique: 0.28, technique: 0.28, vitesse: 0.24, defense: 0.20 }; // Stadium Ace

function scaledBoss(
  name: string,
  level: number,
  shape: PveStatBlock,
  damageMultiplier: number,
  effects?: PveBossEffectId[]
): BossConfig {
  const total = estimateFullEquippedPower(level, "epic");
  return {
    name,
    physique:  Math.round(total * shape.physique),
    technique: Math.round(total * shape.technique),
    vitesse:   Math.round(total * shape.vitesse),
    defense:   Math.round(total * shape.defense),
    damageMultiplier,
    effects,
  };
}

/** Tier from just its number: same [N, N+2] range & flat damage mults as tiers 1-10. */
function makeScaledTier(tier: number, tierName: string): PveTier {
  const lo = tier;
  const hi = tier + 2;
  return makeTier({
    tier, tierName, levelRange: [lo, hi], mobRarity: "rare",
    boss5:  scaledBoss("Rival Captain", lo, BOSS5_SHAPE,  1.15),
    boss10: scaledBoss("Stadium Ace",   hi, BOSS10_SHAPE, 1.25, ["boss_berserker_scaling"]),
  });
}

// ── The 10 hand-authored tiers ────────────────────────────────────────────────
// boss5 = full-epic ref at the tier's MIN level · boss10 = ref at the MAX level.
// Tweak the 4 boss stats freely; keep the total near the ref for a fair fight.
const HAND_TIERS: PveTier[] = [
  makeTier({ tier: 1,  tierName: "First Touch",   levelRange: [1, 3],   mobRarity: "rare",
    boss5:  { name: "Rival Captain", physique: 65, technique: 54, vitesse: 54, defense: 43, damageMultiplier: 1.15 }, // epic ref L1 ≈ 216
    boss10: { name: "Stadium Ace",   physique: 65, technique: 65, vitesse: 56, defense: 47, damageMultiplier: 1.25, effects: ["boss_berserker_scaling"] }, // epic ref L3 ≈ 233
  }),
  makeTier({ tier: 2,  tierName: "Training Ground",     levelRange: [2, 4],   mobRarity: "rare",
    boss5:  { name: "Rival Captain", physique: 68, technique: 56, vitesse: 56, defense: 45, damageMultiplier: 1.15 }, // epic ref L2 ≈ 225
    boss10: { name: "Stadium Ace",   physique: 68, technique: 68, vitesse: 58, defense: 48, damageMultiplier: 1.25, effects: ["boss_berserker_scaling"] }, // epic ref L4 ≈ 242
  }),
  makeTier({ tier: 3,  tierName: "Youth Academy", levelRange: [3, 5],   mobRarity: "rare",
    boss5:  { name: "Rival Captain", physique: 70, technique: 58, vitesse: 58, defense: 47, damageMultiplier: 1.15 }, // epic ref L3 ≈ 233
    boss10: { name: "Stadium Ace",   physique: 70, technique: 70, vitesse: 60, defense: 50, damageMultiplier: 1.25, effects: ["boss_berserker_scaling"] }, // epic ref L5 ≈ 250
  }),
  makeTier({ tier: 4,  tierName: "Practice Pitch",     levelRange: [4, 6],   mobRarity: "rare",
    boss5:  { name: "Rival Captain", physique: 73, technique: 61, vitesse: 61, defense: 48, damageMultiplier: 1.15 }, // epic ref L4 ≈ 242
    boss10: { name: "Stadium Ace",   physique: 72, technique: 72, vitesse: 62, defense: 52, damageMultiplier: 1.25, effects: ["boss_berserker_scaling"] }, // epic ref L6 ≈ 258
  }),
  makeTier({ tier: 5,  tierName: "Academy Derby",   levelRange: [5, 7],   mobRarity: "rare",
    boss5:  { name: "Rival Captain", physique: 75, technique: 63, vitesse: 63, defense: 50, damageMultiplier: 1.15 }, // epic ref L5 ≈ 250
    boss10: { name: "Stadium Ace",   physique: 75, technique: 75, vitesse: 64, defense: 53, damageMultiplier: 1.25, effects: ["boss_berserker_scaling"] }, // epic ref L7 ≈ 267
  }),
  makeTier({ tier: 6,  tierName: "Prospect Showcase",     levelRange: [6, 8],   mobRarity: "rare",
    boss5:  { name: "Rival Captain", physique: 77, technique: 65, vitesse: 65, defense: 52, damageMultiplier: 1.15 }, // epic ref L6 ≈ 258
    boss10: { name: "Stadium Ace",   physique: 77, technique: 77, vitesse: 66, defense: 55, damageMultiplier: 1.25, effects: ["boss_berserker_scaling"] }, // epic ref L8 ≈ 275
  }),
  makeTier({ tier: 7,  tierName: "The Scrimmage",    levelRange: [7, 9],   mobRarity: "rare",
    boss5:  { name: "Rival Captain", physique: 80, technique: 67, vitesse: 67, defense: 53, damageMultiplier: 1.15 }, // epic ref L7 ≈ 267
    boss10: { name: "Stadium Ace",   physique: 80, technique: 80, vitesse: 68, defense: 57, damageMultiplier: 1.25, effects: ["boss_berserker_scaling"] }, // epic ref L9 ≈ 284
  }),
  makeTier({ tier: 8,  tierName: "Draft Combine",  levelRange: [8, 10],  mobRarity: "rare",
    boss5:  { name: "Rival Captain", physique: 83, technique: 69, vitesse: 69, defense: 55, damageMultiplier: 1.15 }, // epic ref L8 ≈ 275
    boss10: { name: "Stadium Ace",   physique: 82, technique: 82, vitesse: 70, defense: 58, damageMultiplier: 1.25, effects: ["boss_berserker_scaling"] }, // epic ref L10 ≈ 292
  }),
  makeTier({ tier: 9,  tierName: "The Call-Up",    levelRange: [9, 11],  mobRarity: "rare",
    boss5:  { name: "Rival Captain", physique: 85, technique: 71, vitesse: 71, defense: 57, damageMultiplier: 1.15 }, // epic ref L9 ≈ 284
    boss10: { name: "Stadium Ace",   physique: 84, technique: 84, vitesse: 72, defense: 60, damageMultiplier: 1.25, effects: ["boss_berserker_scaling"] }, // epic ref L11 ≈ 300
  }),
  makeTier({ tier: 10, tierName: "Debut Match",  levelRange: [10, 12], mobRarity: "rare",
    boss5:  { name: "Rival Captain", physique: 88, technique: 73, vitesse: 73, defense: 58, damageMultiplier: 1.15 }, // epic ref L10 ≈ 292
    boss10: { name: "Stadium Ace",   physique: 87, technique: 87, vitesse: 74, defense: 62, damageMultiplier: 1.25, effects: ["boss_berserker_scaling"] }, // epic ref L12 ≈ 309
  }),
];

// ── Extended tiers 11-30 (auto-scaled) ────────────────────────────────────────
// Thematic names continuing the Rookie→Mythic escalation ("[adjective] [venue]").
// Difficulty rises purely through the climbing level range, exactly like tiers 1-10.
const EXTENDED_TIER_NAMES: string[] = [
  // Act II — Bronze · "Turning Pro"
  "Pro Contract",     // 11
  "Third Division",   // 12
  "Loan Spell",       // 13
  "The Cup Run",      // 14
  "Promotion Push",   // 15 (boss)
  "Second Division",  // 16
  "Transfer Window",  // 17
  "Fan Favorite",     // 18
  "Playoff Final",    // 19
  "Top Flight",       // 20 (boss)
  // Act III — Silver · "The Big Leagues"
  "First XI",         // 21
  "Star Signing",     // 22
  "Derby Day",        // 23
  "Domestic Cup",     // 24
  "Title Challenger", // 25 (boss)
  "Golden Boot Race", // 26
  "Cup Final",        // 27
  "Manager's Trust",  // 28
  "The Decider",      // 29
  "Domestic Glory",   // 30 (boss)
];

const EXTENDED_TIERS: PveTier[] = EXTENDED_TIER_NAMES.map((name, i) => makeScaledTier(11 + i, name));

export const PVE_TIERS: PveTier[] = [...HAND_TIERS, ...EXTENDED_TIERS];

// ── Acts (rank rarity, one step per 10 tiers) ─────────────────────────────────
// The classement rank climbs ONE rarity step per ACT (every 10 tiers), NOT per
// tier — so the row colour changes at a slow, readable pace. Each act is a
// "world" / career stage; the 10 tiers inside share its identity. Add an entry
// when tiers grow past the current top act (each act spans TIERS_PER_ACT tiers).
export const TIERS_PER_ACT = 10;

export interface PveAct {
  name: string;   // rank label (Unranked, Bronze, …) — English UI
  world: string;  // world / career-stage flavour (matches the tier names inside)
  color: string;  // accent used by the classement row + badges
}

export const PVE_ACTS: PveAct[] = [
  { name: "Rookie",   world: "The Academy",       color: "#8A94A6" }, // tiers 1-10
  { name: "Bronze",   world: "Turning Pro",       color: "#CD7F32" }, // tiers 11-20
  { name: "Silver",   world: "The Big Leagues",   color: "#A8B8C8" }, // tiers 21-30
  { name: "Gold",     world: "Continental Stage", color: "#F5B301" }, // 31-40 (future)
  { name: "Platinum", world: "World Stage",       color: "#C8D0D8" }, // 41-50 (future)
  { name: "Diamond",  world: "Legends",           color: "#7DD3FC" }, // 51-60 (future)
];

/** The act (rank rarity + colour) a tier belongs to. Clamps past the top act. */
export function actForTier(tier: number): PveAct {
  const idx = Math.floor((Math.max(1, tier) - 1) / TIERS_PER_ACT);
  return PVE_ACTS[Math.min(idx, PVE_ACTS.length - 1)];
}

export const DEFAULT_TIER = 1;

export function getTier(tier: number): PveTier | undefined {
  return PVE_TIERS.find((t) => t.tier === tier);
}

export function getEncounter(tier: number, stage: number): PveEncounter | undefined {
  return getTier(tier)?.encounters.find((e) => e.stage === stage);
}
